import styled from 'styled-components';

export type AlertTone = 'warning' | 'danger';

const toneColor = (tone: AlertTone): string => (tone === 'danger' ? 'var(--color-error)' : 'var(--color-warning)');

const NavItem = styled.li`
  position: relative;
`;

const BellButton = styled.a`
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

const BellIcon = styled.i`
  font-size: 1.2rem;
`;

const BadgeCount = styled.span<{ $hasExpired: boolean }>`
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background-color: ${({ $hasExpired }) => ($hasExpired ? 'var(--color-error)' : 'var(--color-warning)')};
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
`;

const Panel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 360px;
  max-width: calc(100vw - 24px);
  max-height: 480px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--color-surface-card);
  border: 1px solid var(--color-surface-border);
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.13);
  z-index: 9999;

  @media (max-width: 480px) {
    right: -12px;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-surface-border);
  background-color: var(--color-surface-bg);
`;

const PanelTitle = styled.strong`
  font-size: 0.88rem;
  color: var(--color-text-title);
`;

const CloseButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--color-text-muted);
  font-size: 1.1rem;
  line-height: 1;
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SectionHeader = styled.div<{ $tone: AlertTone }>`
  position: sticky;
  top: 0;
  padding: 8px 16px;
  background-color: var(--color-surface-bg);
  border-bottom: 1px solid var(--color-surface-border);
  border-left: 3px solid ${({ $tone }) => toneColor($tone)};
`;

const SectionTitle = styled.p`
  margin: 0;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-text-title);
`;

const SectionHint = styled.p`
  margin: 2px 0 0;
  font-size: 0.72rem;
  color: var(--color-text-muted);
`;

const LotRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 16px;
  border-bottom: 1px solid var(--color-surface-border);
`;

const LotInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LotName = styled.p`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text-title);
`;

const LotMeta = styled.p`
  margin: 2px 0 0;
  font-size: 0.74rem;
  color: var(--color-text-muted);
`;

const LotWhen = styled.div`
  flex-shrink: 0;
  text-align: right;
`;

const DaysText = styled.p<{ $tone: AlertTone }>`
  margin: 0;
  font-size: 0.74rem;
  font-weight: 700;
  color: ${({ $tone }) => toneColor($tone)};
  white-space: nowrap;
`;

const ExpiryDate = styled.p`
  margin: 2px 0 0;
  font-size: 0.7rem;
  color: var(--color-text-muted);
`;

const MoreLink = styled.button`
  display: block;
  width: 100%;
  padding: 8px 16px;
  border: none;
  border-bottom: 1px solid var(--color-surface-border);
  background: none;
  text-align: left;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--color-accent);
  cursor: pointer;
`;

const AllClear = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 0.84rem;
  color: var(--color-text-muted);

  i {
    display: block;
    margin-bottom: 8px;
    font-size: 1.5rem;
    color: var(--color-success);
  }
`;

const PanelFooter = styled.div`
  padding: 10px 16px;
  border-top: 1px solid var(--color-surface-border);
  background-color: var(--color-surface-bg);
  text-align: center;
`;

const FooterLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-accent);
  cursor: pointer;
`;

const ExpiryAlertsStyled = {
  NavItem,
  BellButton,
  BellIcon,
  BadgeCount,
  Panel,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelBody,
  SectionHeader,
  SectionTitle,
  SectionHint,
  LotRow,
  LotInfo,
  LotName,
  LotMeta,
  LotWhen,
  DaysText,
  ExpiryDate,
  MoreLink,
  AllClear,
  PanelFooter,
  FooterLink,
};

export default ExpiryAlertsStyled;
